import { BlocksUser } from "@/src/models/user.model";
import { getPrimaryRole } from "@/src/utils/role.utils";
import { capitalize } from "@/src/utils/string.utils";
import { Image, Pressable, Text, View } from "react-native";
import { HoverTooltip } from "../../ui-kit/hover-tooltip";
import UserAvatar from "../../ui-kit/user-avatar";

const getRoleBadgeStyle = (role: string) => {
  switch (role.toLowerCase()) {
    case "super-admin":
    case "admin":
      return { bg: "bg-primary", text: "text-white" };
    case "member":
      return { bg: "bg-primary-1", text: "text-primary" };
    default:
      return { bg: "bg-ash", text: "text-slate" };
  }
};

type RoleBadgeCellProps = Readonly<{
  roles: string[];
}>;

export function RoleBadgeCell({ roles }: RoleBadgeCellProps) {
  const primaryRole = getPrimaryRole(roles);
  const extraCount = Math.max(0, roles.length - 1);
  const { bg, text } = getRoleBadgeStyle(primaryRole);

  return (
    <HoverTooltip
      disabled={roles.length <= 1}
      unstyled
      content={
        <View className="bg-chalk border border-ash-3 rounded-md p-2 flex-row flex-wrap gap-1 max-w-[220px]">
          {roles.map((role) => (
            <View key={role} className="bg-ash-3 rounded-full px-2 py-0.5">
              <Text className="text-onyx text-xs">{capitalize(role)}</Text>
            </View>
          ))}
        </View>
      }
    >
      <View className="flex-row items-center gap-1 self-start">
        <View className={`${bg} px-2.5 py-0.5 rounded-full`}>
          <Text className={`${text} text-xs font-medium`}>
            {capitalize(primaryRole)}
          </Text>
        </View>
        {extraCount > 0 && (
          <View className="bg-ash px-1.5 py-0.5 rounded-full">
            <Text className="text-slate text-xs font-medium">
              +{extraCount}
            </Text>
          </View>
        )}
      </View>
    </HoverTooltip>
  );
}

type UserNameCellProps = Readonly<{
  blocksUser: BlocksUser;
  onPress: (blocksUser: BlocksUser) => void;
}>;

export function UserNameCell({ blocksUser, onPress }: UserNameCellProps) {
  const fullName = `${capitalize(blocksUser?.firstName)} ${capitalize(blocksUser?.lastName)}`;

  return (
    <Pressable
      className="flex-row items-center gap-2 flex-1 hover:bg-ash-3 hover:rounded-lg"
      onPress={() => onPress(blocksUser)}
    >
      {blocksUser?.profileImageUrl ? (
        <Image
          source={{ uri: blocksUser?.profileImageUrl }}
          style={{ width: 32, height: 32, borderRadius: 16 }}
        />
      ) : (
        <UserAvatar
          firstName={blocksUser?.firstName}
          lastName={blocksUser?.lastName}
          size={32}
        />
      )}
      <View className="flex-1 min-w-0">
        <HoverTooltip
          className="min-w-0"
          content={<Text className="text-chalk text-xs">{fullName}</Text>}
        >
          <Text className="text-slate" numberOfLines={1} ellipsizeMode="tail">
            {fullName}
          </Text>
        </HoverTooltip>
      </View>
    </Pressable>
  );
}

type UserEmailCellProps = Readonly<{
  email?: string;
}>;

export function UserEmailCell({ email }: UserEmailCellProps) {
  return (
    <HoverTooltip
      className="flex-1"
      disabled={!email}
      content={<Text className="text-chalk text-xs">{email}</Text>}
    >
      <Text className="text-slate" numberOfLines={1} ellipsizeMode="tail">
        {email}
      </Text>
    </HoverTooltip>
  );
}
